import React from 'react';
import { FaEdit, FaTrash, FaFileInvoice } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const TransactionCard = ({
  transaction,
  onEdit,
  onDelete,
  onViewDetails,
  formatCurrency,
  formatDate
}) => {
  const { t } = useTranslation();

  const getTransactionTypeLabel = (type) => {
    const typeLabels = {
      sale: t("Income"),
      purchase: t("Expense"),
      takenLoan: t("Loan Taken"),
      givenLoan: t("Loan Given"),
      loanRepayment: t("Loan Repayment"),
      repaymentReceived: t("Repayment Received"),
      salaryAdvance: t("Salary Advance"),
      salaryPayment: t("Salary Payment"),
      salaryDeduction: t("Salary Deduction"),
      transferIn: t("Transfer In"),
      transferOut: t("Transfer Out")
    };
    return typeLabels[type] || type;
  };

  const getTransactionTypeClass = (type) => {
    if (type === 'sale' || type === 'repaymentReceived' || type === 'transferIn') {
      return 'income-type';
    }
    if (type === 'purchase' || type === 'salaryDeduction' || type === 'transferOut') {
      return 'expense-type';
    }
    if (type === 'takenLoan' || type === 'givenLoan') {
      return 'loan-type';
    }
    if (type === 'loanRepayment') {
      return 'repayment-type';
    }
    if (type === 'salaryAdvance' || type === 'salaryPayment') {
      return 'salary-type';
    }
    return '';
  };

  const isPositive = (type) => {
    // From an accounting perspective, these increase cash
    return type === 'sale' ||
           type === 'takenLoan' ||
           type === 'repaymentReceived' ||
           type === 'transferIn' ||
           type === 'salaryDeduction'; // Money withheld
  };

  // For salary transactions, the amount might be in different fields
  const amount = transaction.payment?.amountPaid ||
                 transaction.payment?.amount ||
                 transaction.amountPaid ||
                 transaction.amount ||
                 transaction.value || 0;
  const transactionType = transaction.payment?.type || transaction.type || 'purchase';
  const category = transaction.payment?.category || transaction.category || getTransactionTypeLabel(transactionType);
  const description = transaction.payment?.description || transaction.description || transaction.details || '';
  const date = transaction.payment?.paymentDate || transaction.paymentDate || transaction.date;
  const invoiceNumber = transaction.invoice?.number || transaction.invoiceNumber || transaction.invoiceId;
  const fieldName = transaction.field?.name || transaction.fieldName;
  const employeeName = transaction.employee?.name || transaction.employee?.username || transaction.employeeName;

  return (
    <div className="transaction-card" onClick={() => onViewDetails(transaction)}>
      <div className="transaction-card-main">
        <div className="transaction-left-section">
          <div className="transaction-category-primary">
            {category}
          </div>
          {description && (
            <div className="transaction-description">
              {description}
            </div>
          )}
          <div className="transaction-date">
            {formatDate(date)}
          </div>
        </div>

        <div className="transaction-right-section">
          <div className={`transaction-amount ${isPositive(transactionType) ? 'positive' : 'negative'}`}>
            {isPositive(transactionType) ? '+' : '-'}{formatCurrency(Math.abs(amount))}
          </div>
          <div className="transaction-actions">
            <button
              className="transaction-action-btn edit"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(transaction);
              }}
              title={t("Edit")}
            >
              <FaEdit />
            </button>
            <button
              className="transaction-action-btn delete"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(transaction);
              }}
              title={t("Delete")}
            >
              <FaTrash />
            </button>
          </div>
        </div>
      </div>

      <div className="transaction-tags">
        {invoiceNumber && (
          <span className="transaction-tag">
            {t("Invoices")}
          </span>
        )}
        {fieldName && (
          <span className="transaction-tag">
            {fieldName}
          </span>
        )}
        {employeeName && (
          <span className="transaction-tag">
            {employeeName}
          </span>
        )}
        {transaction.loan?.relatedParty && (
          <span className="transaction-tag">
            {transaction.loan.relatedParty}
          </span>
        )}
        {transactionType && ['salaryPayment', 'salaryAdvance', 'salaryDeduction'].includes(transactionType) && (
          <span className="transaction-tag">
            {t("Salary")}
          </span>
        )}
        {transactionType && ['takenLoan', 'givenLoan', 'loanRepayment', 'repaymentReceived'].includes(transactionType) && (
          <span className="transaction-tag">
            {t("Loan")}
          </span>
        )}
      </div>
    </div>
  );
};

export default TransactionCard;